import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  IconButton,
  LinearProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import {

  CreateFeedbackRequest,
  CategoryAssignee,
  User
} from '../../types/models';
import { 
  createFeedback, 
  getAllCategoryAssignees
} from '../../services/feedbackService';

interface FeedbackFormModalProps {
  open: boolean;
  onClose: () => void;
  currentUser: User | null;
  setSnackbar?: (snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }) => void;
}

interface FormData {
  category: string;
  subject: string;
  content: string;
  attachments: File[];
}

interface FormErrors {
  category?: string;
  subject?: string;
  content?: string;
}

const FeedbackFormModal: React.FC<FeedbackFormModalProps> = ({ 
  open, 
  onClose, 
  currentUser, 
  setSnackbar 
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [categoryAssignees, setCategoryAssignees] = useState<CategoryAssignee[]>([]);
  const [formData, setFormData] = useState<FormData>({
    category: '',
    subject: '',
    content: '',
    attachments: []
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // 카테고리 담당자 정보 로드
  useEffect(() => {
    if (open) {
      loadCategoryAssignees();
    }
  }, [open]);

  const loadCategoryAssignees = async () => {
    try {
      const assignees = await getAllCategoryAssignees();
      setCategoryAssignees(assignees);
    } catch (error) {
      console.error('카테고리 담당자 정보 로드 실패:', error);
    }
  };

  // 폼 데이터 변경 핸들러
  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // 에러 제거 (FormErrors에 해당 필드가 있는 경우만)
    if ((field === 'category' || field === 'subject' || field === 'content') && errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // 파일 선택 핸들러
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    // 현재 첨부파일 개수 확인 (최대 2개)
    const currentFileCount = formData.attachments.length;
    const remainingSlots = 2 - currentFileCount;
    
    if (remainingSlots <= 0) {
      setSnackbar?.({
        open: true,
        message: '첨부파일은 최대 2개까지만 업로드할 수 있습니다.',
        severity: 'error'
      });
      event.target.value = '';
      return;
    }
    
    // 선택한 파일이 남은 슬롯보다 많으면 제한
    const filesToAdd = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      setSnackbar?.({
        open: true,
        message: `첨부파일은 최대 2개까지만 업로드할 수 있습니다. ${remainingSlots}개만 추가됩니다.`,
        severity: 'info'
      });
    }
    
    const validFiles = filesToAdd.filter(file => {
      // 파일 크기 제한 (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar?.({
          open: true,
          message: `파일 ${file.name}이 너무 큽니다. (최대 10MB)`,
          severity: 'error'
        });
        return false;
      }
      return true;
    });

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));

    // 입력 초기화
    event.target.value = '';
  };

  // 파일 제거 핸들러
  const handleFileRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // 폼 검증
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.category) {
      newErrors.category = '카테고리를 선택해주세요.';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = '제목을 입력해주세요.';
    }
    if (!formData.content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 폼 제출 핸들러
  const handleSubmit = async () => {
    if (!currentUser) {
      setSnackbar?.({
        open: true,
        message: '로그인이 필요합니다.',
        severity: 'error'
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const feedbackRequest: CreateFeedbackRequest = {
        category: formData.category,
        subject: formData.subject.trim(),
        content: formData.content.trim(),
        attachments: formData.attachments.length > 0 ? formData.attachments : undefined
      };

      await createFeedback(feedbackRequest, {
        id: currentUser.id,
        name: currentUser.display_name || currentUser.name || currentUser.email,
        email: currentUser.email
      });

      setSnackbar?.({
        open: true,
        message: '피드백이 성공적으로 전송되었습니다.',
        severity: 'success'
      });

      // 폼 초기화 및 모달 닫기
      handleClose();

    } catch (error) {
      console.error('피드백 전송 실패:', error);
      setSnackbar?.({
        open: true,
        message: '피드백 전송에 실패했습니다. 다시 시도해주세요.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // 모달 닫기 핸들러
  const handleClose = () => {
    if (loading) return;
    
    setFormData({
      category: '',
      subject: '',
      content: '',
      attachments: []
    });
    setErrors({});
    onClose();
  };

  // 선택된 카테고리의 담당자 정보 가져오기
  const selectedAssignee = categoryAssignees.find(assignee => 
    assignee.category === formData.category
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          maxHeight: '90vh'
        },
      }}
    >
      {loading && <LinearProgress />}
      
      <DialogTitle sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        p: 3,
        pb: 1
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%',
          mb: 1
        }}>
          <Typography variant="h6" component="div">
            문의 & 개선 제안 
          </Typography>
          <IconButton 
            onClick={handleClose} 
            size="small"
            sx={{ ml: 1 }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ 
          color: theme.palette.text.secondary,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          mt: -1,
          mb: 1
        }}>
          디자인, 사용성 개선, iCalendar 구독 기능 등. 임직원분들의 신선한 피드백이 실제 기능으로 적용되었습니다.<br/> 
          부담 없이 제안해 주시기를 바라며, 접수된 문의는 메일로 확인 및 해결 답변을 드립니다.<br/> 
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          
          {/* 보내는 사람 (읽기 전용) */}
          <TextField
            fullWidth
            // label="보내는 사람"
            value={currentUser ? `${currentUser.display_name || currentUser.name || '사용자'} (${currentUser.email})` : ''}
            disabled
            slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}
          />
          
          {/* 카테고리 선택 (담당자 정보 포함) */}
          <FormControl fullWidth error={!!errors.category}>
            <InputLabel>문의 유형</InputLabel>
            <Select
              value={formData.category}
              onChange={handleInputChange('category')}
              label="문의 유형"
              disabled={loading}
            >
              {categoryAssignees.map((assignee) => (
                <MenuItem key={assignee.category} value={assignee.category}>
                  {assignee.description}
                </MenuItem>
              ))}
            </Select>
            {errors.category && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.category}
              </Typography>
            )}
            {selectedAssignee && (
                <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: theme.palette.text.secondary }}> 
                담당자: {selectedAssignee.assignee_name}, 
                이메일: {selectedAssignee.assignee_email}
                </Typography>
              )}
          </FormControl>

          {/* 제목 */}
          <TextField
            fullWidth
            label="문의 제목"
            value={formData.subject}
            onChange={handleInputChange('subject')}
            error={!!errors.subject}
            helperText={errors.subject}
            disabled={loading}
            placeholder="간단한 제목을 입력해주세요"
          />

          {/* 내용 */}
          <TextField
            fullWidth
            label="문의 내용"
            value={formData.content}
            onChange={handleInputChange('content')}
            error={!!errors.content}
            helperText={errors.content}
            disabled={loading}
            multiline
            rows={6}
            placeholder="자세한 내용을 입력해주세요"
          />

          {/* 파일 첨부 */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              첨부파일 (선택사항)
            </Typography>
            
            <Box sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
                sx={{ mb: 1 }}
              >
                파일 첨부하기
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                />
              </Button>
              
              <Typography variant="caption" display="block" sx={{ color: theme.palette.text.secondary }}>
                이미지, PDF, 문서 파일만 업로드 가능 (최대 2개, 각 파일 10MB 이하)
              </Typography>
            </Box>

            {/* 선택된 파일 목록 */}
            {formData.attachments.length > 0 && (
              <Stack direction="column" spacing={1}>
                {formData.attachments.map((file, index) => (
                  <Chip
                    key={index}
                    icon={<AttachFileIcon />}
                    label={`${file.name} (${(file.size / 1024).toFixed(1)}KB)`}
                    onDelete={() => handleFileRemove(index)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                    sx={{ justifyContent: 'flex-start' }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1, pt: 0 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          취소
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || !formData.category || !formData.subject.trim() || !formData.content.trim()}
        >
          {loading ? '전송 중...' : '보내기'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackFormModal;